import { Component, computed, inject, input, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import {
  CreatedResponseDto,
  FormSchemaCreateDto,
  FormSchemaDataDto,
  FormSchemaDetailedDto,
  FormTemplateClient,
  FormTypeMiniDto,
  FormTypesClient,
} from '@unity/clients';
import { DropdownFormTypesControlComponent } from '@unity/forms/dropdown-form-types-control/dropdown-form-types-control.component';
import { DropdownWorkflowControlComponent } from '@unity/forms/dropdown-workflow-control/dropdown-workflow-control.component';
import { SurveyCreatorControlComponent } from '@unity/forms/survey-creator-control/survey-creator-control.component';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionCancelSharedComponent } from '@unity/shared/composed/action-cancel-shared/action-cancel-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { defaultObjectFormControl, numericFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class FormTemplateCreateSchema implements FormGroupControlSchema<FormSchemaCreateDto> {
  public name: FormControl<string>;
  public description: FormControl<string>;
  public type_id: FormControl<number>;
  public default_workflow_id: FormControl<number>;
  public data: FormControl<FormSchemaDataDto>;

  public constructor(dto?: FormSchemaDetailedDto) {
    this.data = defaultObjectFormControl<FormSchemaDataDto>({ value: dto?.data ?? {} });
    this.description = stringFormControl({ value: dto?.description });
    this.name = stringFormControl({ value: dto ? dto.name + ' (Clone)' : '' });
    this.type_id = numericFormControl({ value: dto?.type.id });
    this.default_workflow_id = numericFormControl({ value: dto?.default_workflow.id });
  }
}

@Component({
  selector: 'unity-new-form-template-page',
  imports: [
    PageContainerComponent,
    CardNewSharedComponent,
    TextboxControlComponent,
    TextareaControlComponent,
    SurveyCreatorControlComponent,
    DropdownFormTypesControlComponent,
    DropdownWorkflowControlComponent,
    ActionCancelSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-form-template-page.component.html',
  styleUrl: './new-form-template-page.component.scss',
})
export class NewFormTemplatePageComponent extends AbstractNewComponent<FormSchemaCreateDto, FormTemplateCreateSchema, CreatedResponseDto> {
  private readonly formTemplateClient = inject(FormTemplateClient);
  private readonly formTypesClient = inject(FormTypesClient);

  public readonly $formSchemaId = input(undefined, { alias: 'formSchemaId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = computed(() => {
    return 'Create Form Template';
  });

  protected readonly $contentSecondaryHeader = computed(() => {
    const formSchema = this.$formSchema();
    if (formSchema) {
      return 'Clone: ' + formSchema.name;
    }

    return undefined;
  });

  protected override createResourceFn$ = (form: FormGroup<FormTemplateCreateSchema>) => {
    const values = form.getRawValue();
    return this.formTemplateClient.createOne(values);
  };

  private formSchemaResource = rxResource({
    params: () => {
      const formSchemaId = this.$formSchemaId();
      return formSchemaId ? { formSchemaId: formSchemaId } : undefined;
    },
    stream: ({ params: { formSchemaId } }) => this.formTemplateClient.getOneAsDetailed(formSchemaId),
  });

  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => {
    return ['/form-template', response.id];
  };

  protected override newControlsFn() {
    const formSchema = this.$formSchema();
    return new FormTemplateCreateSchema(formSchema);
  }

  protected formTypesResource = rxResource({
    stream: () => this.formTypesClient.getAllAsMini(),
  });

  protected readonly $formSchema = computed(() => {
    if (!this.formSchemaResource.hasValue()) {
      return undefined;
    }
    return this.formSchemaResource.value();
  });

  protected readonly $isLoading = computed(() => {
    const formSchemaId = this.$formSchemaId();
    const formSchemaIsLoading = this.formSchemaResource.isLoading();

    return !formSchemaId || formSchemaIsLoading;
  });

  protected readonly $selectableFormTypes: Signal<FormTypeMiniDto[]> = computed(() => {
    const types: FormTypeMiniDto[] = this.formTypesResource.value()?.data ?? [];
    return types;
  });

  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
